import { useDebounceValue } from '@griply/hooks';
import { getExternalEventEditable } from '@griply/utils';
import { addDays, endOfDay, startOfDay } from 'date-fns';
import { useMemo } from 'react';
import { useExternalCalendarEventsQuery } from 'shared/hooks/queries/use-external-calendar-events-query';
import { Timestamp } from 'shared/types/timestamp';

import { mapExternalEventToFullCalendarEvent } from '../utils/map-external-event-to-full-calendar-event';
import { useExternalCalendarConnectionsWithCalendars } from './use-external-calendar-connections-with-calendars';

export const useExternalCalendarFullCalendarEvents = (
  startDate: Timestamp,
  rangeInDays: number,
) => {
  const _startDate = useDebounceValue(startOfDay(startDate).toISOString());
  const _endDate = useDebounceValue(
    addDays(endOfDay(startDate), rangeInDays).toISOString(),
  );
  const { data } = useExternalCalendarEventsQuery(_startDate, _endDate);

  // already subscribed at boot by EventFormProvider, so the calendars are a cache read
  const { connections, calendars } =
    useExternalCalendarConnectionsWithCalendars();

  return useMemo(
    () =>
      data?.map((event) =>
        mapExternalEventToFullCalendarEvent(
          event,
          getExternalEventEditable(event, { connections, calendars }),
        ),
      ) ?? [],
    [calendars, connections, data],
  );
};
